/**
 * Lien de partage.
 *
 * Trois façons de transmettre le lien, du plus rapide au plus manuel :
 *
 * - le **QR code**, qui supprime toute friction pour un transfert téléphone ↔ ordinateur —
 *   c'est l'usage le plus fréquent et le plus pénible autrement ;
 * - le **partage natif**, quand le navigateur l'expose : il ouvre le sélecteur du système,
 *   donc les applications que l'utilisateur a réellement installées ;
 * - le **presse-papiers**, avec repli sur la sélection du texte si l'API est refusée
 *   (elle exige un contexte sécurisé et peut être bloquée par une politique).
 */

import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import { QrCode } from './QrCode.js';

export function ShareLink({ url }: { url: string }): React.JSX.Element {
  const translator = useT();
  const [copied, setCopied] = useState(false);

  // Le message de confirmation disparaît tout seul : pas besoin d'un second clic.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Presse-papiers refusé : on sélectionne le texte pour que Ctrl/⌘+C fonctionne.
      const input = document.getElementById('share-url') as HTMLInputElement | null;
      input?.select();
    }
  };

  const canShareNatively =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const shareNatively = (): void => {
    void navigator.share({ url }).catch(() => {
      // L'utilisateur a fermé le sélecteur de partage : rien à signaler.
    });
  };

  const hint = translator.t('share.hint');

  return (
    <div className="stack">
      <div className="share-url">
        <label className="sr-only" htmlFor="share-url">
          {translator.t('share.label')}
        </label>
        <input id="share-url" readOnly value={url} onFocus={(e) => e.target.select()} />
        <button type="button" className="btn btn-primary" onClick={() => void copy()}>
          {copied ? translator.t('share.copied') : translator.t('share.copy')}
        </button>
        {canShareNatively && (
          <button type="button" className="btn" onClick={shareNatively}>
            {translator.t('share.share')}
          </button>
        )}
      </div>

      <div className="qr-panel">
        <QrCode value={url} label={translator.t('share.qrLabel')} />
        <p className="faint">Scannez ce code avec l’appareil photo du téléphone.</p>
      </div>

      <p className="faint" style={{ margin: 0 }}>
        {hint}
      </p>
    </div>
  );
}
